import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Subject, Observable } from 'rxjs';
import { Asset } from 'src/app/_models/asset';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AssetAttribute } from 'src/app/_models/assetAttribute';
import { AssetView } from 'src/app/_models/assetView';
import { AssetForBulkDelete, AssetForReplacement } from 'src/app/_models/assetForReplacement';
import { AssetForWorkOrder } from 'src/app/_models/assetForWorkOrder';
import { TransactionHistoryDetail } from 'src/app/_models/transactionHistoryDetail';
import { ComponentDto } from 'src/app/_models/componentDto';
import { AssetSplit } from 'src/app/_models/assetSplit';
import { PanelBarSelectEvent } from '@progress/kendo-angular-layout';
import { AssetSearchParams } from 'src/app/_models/assetSearchParams';

@Injectable({
   providedIn: 'root',
})
export class AssetService {
   baseUrl = environment.apiUrl;
   assetSelected = new Subject<Asset>();
   ImportSelected = new Subject<Asset>();
   WipComponentSelected = new Subject<ComponentDto>();
   AssetSplitSelected = new Subject<AssetSplit>();
   leaseAssetSelected = new Subject<[Asset, number]>();
   constructor(private http: HttpClient) {}

   changeEditState(a: Asset) {
      this.assetSelected.next(a);
   }

   changeImportState(asset: Asset) {
      this.ImportSelected.next(asset);
   }

   changeLeaseAssetEditState(a: Asset, leaseId: number) {
      this.leaseAssetSelected.next([a, leaseId]);
   }

   changeAssetSplitEditState(asset: AssetSplit) {
      this.AssetSplitSelected.next(asset);
   }

   changeWipState(asset: ComponentDto) {
      this.WipComponentSelected.next(asset);
   }

   getAssetFilter(queryStr: string): Observable<Asset[]> {
      return this.http.get<Asset[]>(this.baseUrl + 'asset/filter' + '?' + queryStr);
   }

   getAssetSearch(queryStr: string): Observable<Asset[]> {
      return this.http.get<Asset[]>(this.baseUrl + 'asset/search/' + queryStr);
   }

   getMultiAssetSearch(filterParams: any): Observable<Asset[]> {
      const params = new HttpParams().set('page', '1').set('pageSize', '10').set('filter', `(${filterParams})`);

      return this.http.get<Asset[]>(`${this.baseUrl}asset/filter?`, { params });
   }

   getAssetsFull(): Observable<Asset[]> {
      return this.http.get<Asset[]>(this.baseUrl + 'asset/full');
   }

   getAssetsForSearch(searchTerm: string): Observable<Asset[]> {
      return this.http.get<Asset[]>(this.baseUrl + 'asset/searchten/' + searchTerm);
   }

   getAsset(id): Observable<Asset> {
      return this.http.get<Asset>(this.baseUrl + 'asset/' + id);
   }

   getAssetByBarcode(barcode): Observable<Asset> {
      return this.http.get<Asset>(this.baseUrl + 'asset/GetAssetByBarcodeForWip/' + encodeURIComponent(barcode));
   }

   getAssetForReplacement(id): Observable<AssetForReplacement> {
      return this.http.get<AssetForReplacement>(this.baseUrl + 'asset/replacement/' + id);
   }

   getAssetsByCustodian(): Observable<Asset[]> {
      return this.http.get<Asset[]>(this.baseUrl + 'asset/custodian');
   }

   getAssetView(id): Observable<AssetView> {
      return this.http.get<AssetView>(this.baseUrl + 'asset/view/' + id);
   }

   getAssetForWorkOrder(id): Observable<AssetForWorkOrder> {
      return this.http.get<AssetForWorkOrder>(this.baseUrl + 'asset/workorder/' + id);
   }

   getAcquisitionIdByAssetCode(assetCode): Observable<number> {
      return this.http.get<number>(this.baseUrl + 'asset/acquisitionid/' + assetCode);
   }

   getAssetAttributesByTypeCode(typeCode): Observable<AssetAttribute[]> {
      return this.http.get<AssetAttribute[]>(this.baseUrl + 'asset/assetattributes/' + typeCode);
   }

   GetAssetHistoryDetail(id: number, tranType: string): Observable<TransactionHistoryDetail> {
      return this.http.get<TransactionHistoryDetail>(this.baseUrl + 'asset/transactionhistorydetail/' + id + '/' + encodeURIComponent(tranType));
   }

   updateAsset(id: number, asset: Asset) {
      return this.http.put(this.baseUrl + 'asset/' + id, asset);
   }

   createAsset(asset: Asset) {
      return this.http.post(this.baseUrl + 'asset', asset);
   }

   getPossibleAssetStatusList(): Observable<string[]> {
      return this.http.get<string[]>(this.baseUrl + 'asset/getPossibleAssetStatusList');
   }

   setAssetToInactive(id: number) {
      return this.http.delete(this.baseUrl + 'asset/' + id);
   }
   export(WithAcq: boolean, isImpAssetERP: boolean, isLeaseAsset: boolean): Observable<Blob> {
      // tslint:disable-next-line:max-line-length
      return this.http.get(this.baseUrl + 'asset/export/' + WithAcq + '/' + isImpAssetERP, {
         responseType: 'blob',
      });
   }

   groupExport(group: string): Observable<Blob> {
      return this.http.get(this.baseUrl + 'asset/groupExport/' + group, {
         responseType: 'blob',
      });
   }

   custodianExport(group: string): Observable<Blob> {
      return this.http.get(this.baseUrl + 'asset/custodianExport/' + group, {
         responseType: 'blob',
      });
   }

   noGLAccountExport(group: string): Observable<Blob> {
      return this.http.get(this.baseUrl + 'asset/noGLAccountExport/' + group, {
         responseType: 'blob',
      });
   }

   exportStatus(status: string): Observable<Blob> {
      // tslint:disable-next-line:max-line-length
      return this.http.get(this.baseUrl + 'asset/exportStatus/' + status, {
         responseType: 'blob',
      });
   }

   bulkExport(): Observable<Blob> {
      return this.http.get(this.baseUrl + 'asset/bulkExport/', {
         responseType: 'blob',
      });
   }

   exportGreatPlains(): Observable<Blob> {
      return this.http.get(this.baseUrl + 'asset/exportGreatPlains', {
         responseType: 'blob',
      });
   }

   getBookValue(assetCode: number, effectiveFrom: string): Observable<number> {
      return this.http.get<number>(this.baseUrl + 'asset/bookvalue/' + assetCode + '/' + encodeURIComponent(effectiveFrom));
   }

   getAssetsByAcquisitionAccountNo(classificationAccountId: string): Observable<Asset[]> {
      return this.http.get<Asset[]>(this.baseUrl + 'asset/getassetsacquisitionaccountno/' + classificationAccountId);
   }

   getAssetsByProfitIncomeAccountNo(classificationAccountId: string): Observable<Asset[]> {
      return this.http.get<Asset[]>(this.baseUrl + 'asset/getassetsprofitincomeaccountno/' + classificationAccountId);
   }

   getAssetCountByComponentType(typeId: string): Observable<number> {
      return this.http.get<number>(this.baseUrl + 'asset/assetCountByComponentType/' + typeId);
   }

   getAssetCountByDescriptorType(typeId: string): Observable<number> {
      return this.http.get<number>(this.baseUrl + 'asset/assetCountByDescriptorType/' + typeId);
   }
   removeBulk(assets: AssetForBulkDelete[]) {
      return this.http.put(this.baseUrl + 'asset/bulkdelete', assets);
   }

   getFunctionalLocation1List(searchParams: AssetSearchParams): Observable<string[]> {
      const params = new HttpParams()
         .set('functionalLocation1', searchParams.functionalLocation1)
         .set('functionalLocation2', searchParams.functionalLocation2)
         .set('functionalLocation3', searchParams.functionalLocation3)
         .set('componentType', searchParams.componentType);
      return this.http.get<string[]>(this.baseUrl + 'asset/getFunctionalLocation1List', { params });
   }
   getFunctionalLocation2List(searchParams: AssetSearchParams): Observable<string[]> {
      const params = new HttpParams()
         .set('functionalLocation1', searchParams.functionalLocation1)
         .set('functionalLocation2', searchParams.functionalLocation2)
         .set('functionalLocation3', searchParams.functionalLocation3)
         .set('componentType', searchParams.componentType);
      return this.http.get<string[]>(this.baseUrl + 'asset/getFunctionalLocation2List', { params });
   }
   getFunctionalLocation3List(searchParams: AssetSearchParams): Observable<string[]> {
      const params = new HttpParams()
         .set('functionalLocation1', searchParams.functionalLocation1)
         .set('functionalLocation2', searchParams.functionalLocation2)
         .set('functionalLocation3', searchParams.functionalLocation3)
         .set('componentType', searchParams.componentType);
      return this.http.get<string[]>(this.baseUrl + 'asset/getFunctionalLocation3List', { params });
   }

   assetSplitExport(): Observable<Blob> {
      return this.http.get(this.baseUrl + 'asset/assetSplitExport', {
         responseType: 'blob',
      });
   }

   assetSplitCreate(assetToSplit: AssetSplit) {
      return this.http.post(this.baseUrl + 'asset/assetSplitCreate', assetToSplit);
   }

   deleteAssetSplit(id: number) {
      return this.http.delete(this.baseUrl + 'asset/assetSplitDelete/' + id);
   }

   approveSplit(id: number) {
      return this.http.put(this.baseUrl + 'asset/assetSplitApprove/' + id, null);
   }
}
